import React, { type ReactNode } from "react";
import {
  Icon,
  Button as ChakraButton,
  type IconProps,
  type ButtonProps as ChakraButtonProps,
  type LinkProps as ChakraLinkProps,
} from "@chakra-ui/react";
import Link from "next/link";
import { type Theme } from "contexts/color-theme";
import { ChevronRightIcon } from "../icons";

type BaseButtonProps = {
  /**
   * Determines if the button has a right chevron icon
   */
  hasRightChevron?: boolean;

  /**
   * The URL the button should navigate to.
   * If provided, the button will render as an anchor tag.
   */
  href?: string;

  /**
   * Button text.
   */
  children: ReactNode;

  /**
   * Button variant, which determines the purpose the button is used for and its appearance.
   *
   * @default "primary"
   */
  variant?: "primary" | "secondary" | "darkBlue";

  /**
   * Determines if the button has a pulse animation on active state. Only displayed for `primary` buttons.
   * Use the light pulse for light backgrounds and the dark pulse for dark backgrounds.
   *
   * @default undefined
   */
  pulse?: Theme | undefined;

  /**
   * Determines the height of the button.
   *
   * @default "md"
   */
  size?: "sm" | "md" | "lg";

  /**
   * Icon that's displayed on the left.
   */
  leftIcon?: (props: IconProps) => JSX.Element;
};

export type ButtonProps = BaseButtonProps &
  Omit<ChakraButtonProps, "children" | "variant" | "size" | "leftIcon" | "rightIcon"> &
  Omit<ChakraLinkProps, "href">;

export function Button({
  "aria-label": ariaLabel,
  hasRightChevron,
  variant = "primary",
  size = "md",
  children,
  leftIcon,
  ...props
}: ButtonProps) {
  const pulse = variant === "primary" ? props.pulse : undefined;

  return (
    <ChakraButton
      as={props.href ? Link : undefined}
      aria-label={ariaLabel ?? children?.toString()}
      className="group"
      data-pulse={pulse}
      data-left-icon={leftIcon ? "true" : undefined}
      data-right-icon={hasRightChevron ? "true" : undefined}
      variant={variant}
      size={size}
      iconSpacing={1}
      leftIcon={leftIcon ? <Icon as={leftIcon} boxSize={`icon.${size}`} /> : undefined}
      rightIcon={hasRightChevron ? <ChevronRightIcon boxSize={`icon.${size}`} /> : undefined}
      {...props}
    >
      {children}
    </ChakraButton>
  );
}
